<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="pagos"
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="options"
      class="elevation-1"
    >
      <template slot="items" slot-scope="props">
        <td>
          <div v-if="props.item.personas && props.item.personas[0]">
            <strong
              >{{ props.item.personas[0].nombre }}
              {{ props.item.personas[0].apellido }}</strong
            >
            <br />
            <small class="grey--text"
              >DNI: {{ props.item.personas[0].documento }}</small
            >
          </div>
          <span v-else class="grey--text">Sin datos</span>
        </td>

        <td>
          <v-chip
            :color="getEstadoColor(props.item.state)"
            text-color="white"
            small
          >
            {{ getEstadoLabel(props.item.state) }}
          </v-chip>
        </td>

        <td>{{ formatearFecha(props.item.payment_receipt_uploaded_at) }}</td>

        <td>
          {{
            props.item.modalidad && props.item.modalidad.nombre
              ? props.item.modalidad.nombre
              : "N/A"
          }}
        </td>

        <td>
          <div v-if="props.item.payment_receipt_url">
            <v-btn
              color="success"
              small
              outline
              @click="downloadReceipt(props.item)"
              :loading="downloadId === props.item.id"
            >
              <v-icon left small>file_download</v-icon>
              Descargar
            </v-btn>
            <br />
            <small class="grey--text">
              {{ getFileExtension(props.item.payment_receipt_mime) }} -
              {{ formatFileSize(props.item.payment_receipt_size) }}
            </small>
          </div>
          <span v-else class="grey--text">Sin comprobante</span>
        </td>
      </template>

      <template slot="no-data">
        <div class="text-xs-center pa-3">
          <span v-if="loading">Cargando...</span>
          <span v-else>No hay pagos para mostrar</span>
        </div>
      </template>
    </v-data-table>

    <v-snackbar
      v-model="errorSnackbar.show"
      color="error"
      top
      right
    >
      {{ errorSnackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import api from "@/api/inscripcion";
import moment from "moment";

export default {
  name: "PagosTabla",
  props: {
    eventoId: {
      type: [String, Number],
      required: true,
    },
    filtros: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      downloadId: null,
      loading: false,
      pagos: [],
      totalItems: 0,
      errorSnackbar: {
        show: false,
        message: "",
      },
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ["payment_receipt_uploaded_at"],
        sortDesc: [true],
      },
      headers: [
        { text: "Participante", value: "participante", sortable: false },
        { text: "Estado", value: "state", sortable: true },
        {
          text: "Fecha Subida",
          value: "payment_receipt_uploaded_at",
          sortable: true,
        },
        { text: "Modalidad", value: "modalidad", sortable: false },
        { text: "Comprobante", value: "comprobante", sortable: false },
      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.cargarDatos();
      },
      deep: true,
    },
    filtros: {
      handler() {
        this.options.page = 1;
        this.cargarDatos();
      },
      deep: true,
    },
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    async downloadReceipt(pago) {
      this.downloadId = pago.id;

      try {
        const response = await api.downloadPaymentReceipt(pago.id);

        const blob = new Blob([response.data], {
          type: pago.payment_receipt_mime || "application/octet-stream",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const extension = this.getMimeExtension(pago.payment_receipt_mime);
        const participante =
          pago.personas && pago.personas[0]
            ? `${pago.personas[0].nombre}_${pago.personas[0].apellido}`
            : "comprobante";

        link.download = `${participante}_${pago.id}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error descargando comprobante:", error);
        this.showError(this.getErrorMessage(error));
      } finally {
        this.downloadId = null;
      }
    },

    getErrorMessage(error) {
      if (error.response) {
        switch (error.response.status) {
          case 404:
            return "El archivo del comprobante no se encuentra disponible";
          case 403:
            return "No tiene permisos para descargar este archivo";
          case 500:
            return "Error interno del servidor al descargar el archivo";
          default:
            return "Error al descargar el archivo";
        }
      } else if (error.request) {
        return "No se pudo conectar con el servidor";
      } else {
        return "Error inesperado al procesar la descarga";
      }
    },

    showError(message) {
      this.errorSnackbar.message = message;
      this.errorSnackbar.show = true;
    },
    getMimeExtension(mime) {
      const extensions = {
        "application/pdf": "pdf",
        "image/jpeg": "jpg",
        "image/png": "png",
        "image/jpg": "jpg",
      };
      return extensions[mime] || "file";
    },
    async cargarDatos() {
      this.loading = true;

      try {
        const params = {
          evento_id: this.eventoId,
          page: this.options.page,
          per_page: this.options.itemsPerPage,
          ...this.filtros,
        };

        if (this.options.sortBy.length > 0) {
          params.sort_by = this.options.sortBy[0];
          params.sort_desc = this.options.sortDesc[0];
        }

        const response = await api.getPayments(params);

        if (response.data) {
          this.pagos = response.data;
          this.totalItems = response.total;
        } else {
          this.pagos = response;
          this.totalItems = response.length;
        }
      } catch (error) {
        console.error("Error cargando pagos:", error);
      } finally {
        this.loading = false;
      }
    },

    getEstadoColor(estado) {
      const colores = {
        pago_realizado: "success",
        pago_en_revision: "warning",
        pago_rechazado: "error",
        pago_pendiente: "info",
      };
      return colores[estado] || "grey";
    },

    getEstadoLabel(estado) {
      const labels = {
        pago_realizado: "Aprobado",
        pago_en_revision: "En Revisión",
        pago_rechazado: "Rechazado",
        pago_pendiente: "Pendiente",
      };
      return labels[estado] || estado;
    },

    formatearFecha(fecha) {
      return fecha ? moment(fecha).format("DD/MM/YYYY HH:mm") : "N/A";
    },

    getFileExtension(mime) {
      const extensions = {
        "application/pdf": "PDF",
        "image/jpeg": "JPG",
        "image/png": "PNG",
        "image/jpg": "JPG",
      };
      return extensions[mime] || "Archivo";
    },

    formatFileSize(bytes) {
      if (!bytes) return "N/A";
      return (bytes / 1024).toFixed(1) + " KB";
    },
  },
};
</script>